import produce from "immer";
import { Reducer } from "redux";
import { createTransform, persistReducer } from "redux-persist";
import storage from "redux-persist/es/storage";
import Time from "../../lib/Time/Time";
import {
  DELETE_STALE_KEG_NOTIFICATION,
  EInsightsNotificationType,
  IInsightsNotification,
  IInsightsNotificationsState,
  InsightsNotificationsActionTypes,
  REQUEST_INSIGHTS_NOTIFICATIONS,
  REQUEST_INSIGHTS_NOTIFICATIONS_ERROR,
  REQUEST_INSIGHTS_NOTIFICATIONS_SUCCESS,
  SET_INSIGHT_FEEDBACK,
  SET_INSIGHT_SEEN,
  SET_INSIGHTS_NOTIFICATIONS_CLOSE,
  SET_INSIGHTS_NOTIFICATIONS_OPEN,
  SET_INSIGHTS_STORIES_CLOSE,
  SET_INSIGHTS_STORIES_OPEN
} from "./types";
import InsightComponentMap, {
  InsightComponentTestMap
} from "../../components/common/insights/InsightItemMap";

export const initialState: IInsightsNotificationsState = {
  error: false,
  insightsNotifications: {},
  insightStoryData: null,
  insightsRead: {},
  insightsNotificationOpen: false,
  loading: true,
  selectedTab: "FAVOURITE"
};

const reducer: Reducer<IInsightsNotificationsState, InsightsNotificationsActionTypes> = (
  state = initialState,
  action: InsightsNotificationsActionTypes
) =>
  produce(state, draft => {
    switch (action.type) {
      case REQUEST_INSIGHTS_NOTIFICATIONS:
        draft.error = false;
        draft.loading = true;
        break;

      case REQUEST_INSIGHTS_NOTIFICATIONS_SUCCESS:
        try {
          Object.keys(action.payload.data.insights).forEach((id: string) => {
            const outletInsights = [
              ...action.payload.data.insights[id]
                .filter(insight => {
                  return (
                    EInsightsNotificationType[insight.type] &&
                    ((InsightComponentMap[
                      // @ts-ignore
                      insight.type
                    ] as any) ||
                      (InsightComponentTestMap[
                        insight.type as keyof typeof InsightComponentTestMap
                      ] &&
                        (action.payload.userId === "55f62aac-2011-4c3f-8d47-d377c0663c4a" ||
                          action.payload.userId === "bafcc3ba-2366-412f-967d-889945a860c2")))
                  );
                })
                .filter(Boolean)
                .map(i => ({
                  ...i,
                  customFields: JSON.parse(i.customFields as any),
                  details: i.details.map(d => ({
                    ...d,
                    customFields: JSON.parse(d.customFields as any)
                  }))
                })),
              ...(state.insightsNotifications[id] || [])
            ];

            draft.insightsNotifications[id] = outletInsights.sort((insightA, insightB) =>
              insightA.createDateTime > insightB.createDateTime ? 1 : -1
            );

            if (draft.insightsNotifications[id].length === 0) {
              delete draft.insightsNotifications[id];
            }
          });
        } catch (e) {
          // console.log(e);
        }
        draft.loading = false;
        draft.error = false;
        break;

      case REQUEST_INSIGHTS_NOTIFICATIONS_ERROR:
        draft.error = true;
        draft.loading = false;
        break;

      case SET_INSIGHTS_NOTIFICATIONS_OPEN:
        draft.insightsNotificationOpen = true;
        break;

      case SET_INSIGHTS_NOTIFICATIONS_CLOSE:
        draft.insightsNotificationOpen = false;
        const { insights } = action.payload;

        if (insights) {
          Object.keys(insights).forEach(outletId => {
            draft.insightsRead[outletId] = insights[outletId].map(({ id, createDateTime }) => ({
              id,
              createDateTime
            }));
          });
        }
        break;
      case SET_INSIGHTS_STORIES_OPEN:
        draft.insightStoryData = action.payload;
        break;

      case SET_INSIGHTS_STORIES_CLOSE:
        draft.insightStoryData = null;
        break;

      case SET_INSIGHT_FEEDBACK:
        const insight = draft.insightsNotifications[action.payload.locationId]?.find(
          i => i.id === action.payload.insightId
        );

        if (insight) {
          insight.feedback = action.payload.feedback;
        }
        break;

      case SET_INSIGHT_SEEN:
        const currentInsight = draft.insightsNotifications[action.payload.locationId]?.find(
          i => i.id === action.payload.insightId
        );

        if (currentInsight) {
          if (draft.insightsRead[action.payload.locationId]) {
            draft.insightsRead[action.payload.locationId].push({
              id: currentInsight.id,
              createDateTime: currentInsight.createDateTime
            });
          } else {
            draft.insightsRead[action.payload.locationId] = [
              {
                id: currentInsight.id,
                createDateTime: currentInsight.createDateTime
              }
            ];
          }
        }
        break;

      case DELETE_STALE_KEG_NOTIFICATION:
        // eslint-disable-next-line array-callback-return
        Object.keys(draft.insightsNotifications).map(key => {
          if (key === action.payload.locationId) {
            const temp = draft.insightsNotifications[key].map(insightNoti => {
              if (insightNoti.type === EInsightsNotificationType.STALE_KEG) {
                // eslint-disable-next-line no-param-reassign
                (insightNoti.customFields as any).pressureChamberIds = (insightNoti.customFields as any).pressureChamberIds.filter(
                  (pcId: string) => pcId !== action.payload.pressureChamberId
                );
                return (insightNoti.customFields as any).pressureChamberIds.length > 0
                  ? insightNoti
                  : null;
              }
              return insightNoti;
            });

            draft.insightsNotifications[key] = temp.filter(Boolean) as any;
          }
        });
        break;
      default:
        break;
    }
  });

const insightsTransform = createTransform(
  (inboundState: any, key: any) => {
    if (key === "insightsRead") {
      try {
        const insights: any = {};
        (Object.keys(inboundState || {}) || []).forEach((insightKey: string) => {
          insights[insightKey] = inboundState[insightKey]
            .map(({ id, createDateTime }: IInsightsNotification) => {
              if (new Time(createDateTime).diff(new Time()) <= 30) {
                return {
                  id,
                  createDateTime
                };
              }
              return undefined;
            })
            .filter(Boolean);
        });
        return insights;
      } catch (e) {
        return {};
      }
    }
    return inboundState;
  },
  // transform state being rehydrated
  outboundState => {
    return outboundState;
  },
  { whitelist: ["insightsNotifications", "insightsRead"] }
);

export default persistReducer<IInsightsNotificationsState>(
  {
    key: "insightsNotifications",
    storage,
    whitelist: ["insightsRead"],
    transforms: [insightsTransform]
  },
  reducer
);
